import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, TrendingUp, TrendingDown, Wallet, LogOut, ArrowUpRight, ArrowDownRight, Copy, User, Calendar as CalIcon, Phone, Mail, MapPin, Building2, Globe, ShieldCheck, Activity, Download, Loader2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from 'recharts';
import { Calendar } from '@/components/ui/calendar';
import QRCode from 'react-qr-code';
import { KycFormSection } from '@/components/KycFormSection';
import { InvestmentPlansPanel } from '@/components/InvestmentPlansPanel';
import { ProfileCompleteness } from '@/components/ProfileCompleteness';
import { BalanceHero } from '@/components/investor/BalanceHero';
import { QuickActionGrid } from '@/components/investor/QuickActionGrid';
import { EnablePushButton } from '@/components/common/EnablePushButton';
import { formatClientCode } from '@/lib/clientCode';
import { GrowwBottomNav } from '@/components/investor/GrowwBottomNav';
import { PnlHeatmapCalendar } from '@/components/investor/PnlHeatmapCalendar';
import { MobileHeader } from '@/components/investor/MobileHeader';
import { TradeRowCard, SegmentedTradeFilter } from '@/components/investor/TradeRowCard';

// Stable recharts style constants (prevent inline-object re-renders)
const AXIS_TICK = { fontSize: 11, fill: '#64748B' };
const AXIS_TICK_SM = { fontSize: 10, fill: '#64748B' };
const CHART_TOOLTIP_STYLE = { borderRadius: '8px', border: '1px solid #CBD5E1', fontSize: '12px' };
const BAR_RADIUS = [4, 4, 0, 0];

const MetricCard = ({ title, value, icon: Icon, color = 'text-brand-primary', testId, subtitle }) => (
  <Card className="border border-brand-border bg-white metric-card" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary mb-1">{title}</p>
          <p className="font-heading text-2xl tracking-tight text-brand-text-primary">${typeof value === 'number' ? value.toLocaleString(undefined, {minimumFractionDigits: 2}) : value}</p>
          {subtitle && <p className="text-[10px] text-brand-text-secondary mt-1.5 leading-tight">{subtitle}</p>}
        </div>
        <div className={`w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center`}>
          <Icon size={18} strokeWidth={1.5} className={color} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function InvestorPortal() {
  // (round2 helper removed — no longer needed after dropping 50% share column)
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [depositSettings, setDepositSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [depositForm, setDepositForm] = useState({ amount: '', currency: 'USDT', payment_method: 'crypto_usdt_bep20', transaction_hash: '', upi_ref: '', bank_ref: '', notes: '' });
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', currency: 'USDT', payment_method: 'crypto_usdt_bep20', wallet_address: '', upi_id: '', bank_account_name: '', bank_account_no: '', bank_ifsc: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(83);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [period, setPeriod] = useState('current_month');
  const [statementLoading, setStatementLoading] = useState(false);
  const [kycRecord, setKycRecord] = useState(null);
  const [hasSignedAgreement, setHasSignedAgreement] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tradeFilter, setTradeFilter] = useState('all');

  const availableMonths = (() => {
    const entries = data?.pnl_entries || [];
    const set = new Set();
    entries.forEach(e => {
      if (e.date) {
        const d = new Date(e.date);
        if (!isNaN(d)) {
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          set.add(key);
        }
      }
    });
    return Array.from(set).sort().reverse().map(k => {
      const [yr, mo] = k.split('-');
      const lbl = new Date(parseInt(yr), parseInt(mo) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
      return { value: k, label: lbl };
    });
  })();

  const handleDownloadStatement = async () => {
    setStatementLoading(true);
    try {
      const params = period === 'all' ? {} : period === 'current_month' ? { period: 'current_month' } : { period: 'month', month: period };
      const res = await api.get('/exports/client-statement/me/download', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const periodTag = period === 'all' ? 'alltime' : period === 'current_month' ? new Date().toISOString().slice(0, 7) : period;
      a.download = `statement-${data?.client?.client_id || 'me'}-${periodTag}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
    } catch (err) {
      console.error('[InvestorPortal] statement download failed:', err);
      toast.error(formatError(err.response?.data?.detail) || 'Failed to download statement');
    } finally {
      setStatementLoading(false);
    }
  };
  const [supportInfo, setSupportInfo] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, depRes, calRes, supRes, rateRes, payRes, pmRes] = await Promise.all([
        api.get('/dashboard/investor-by-user'),
        api.get('/deposit-settings').catch(() => ({ data: {} })),
        api.get('/pnl/calendar').catch(() => ({ data: [] })),
        api.get('/clients/support/info').catch(() => ({ data: null })),
        api.get('/exchange-rate').catch(() => ({ data: { usdt_inr: 83 } })),
        api.get('/payment-details').catch(() => ({ data: null })),
        api.get('/payment-settings/active-methods').catch(() => ({ data: null }))
      ]);
      setData(dashRes.data);
      setDepositSettings(depRes.data);
      setCalendarData(calRes.data);
      setSupportInfo(supRes.data);
      setExchangeRate(rateRes.data?.usdt_inr || 83);
      setPaymentDetails(payRes.data);
      setPaymentConfig(pmRes.data);

      // Fetch KYC + agreements for completeness widget
      if (dashRes.data?.client?.id) {
        const [kycRes, agRes] = await Promise.all([
          api.get(`/kyc/client/${dashRes.data.client.id}`).catch(() => ({ data: null })),
          api.get(`/agreements`, { params: { client_id: dashRes.data.client.id } }).catch(() => ({ data: [] })),
        ]);
        setKycRecord(kycRes.data);
        setHasSignedAgreement(Array.isArray(agRes.data) && agRes.data.some(a => a.status === 'signed'));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleDeposit = async () => {
    if (!depositForm.amount || parseFloat(depositForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      await api.post('/funds/client-deposit', { ...depositForm, amount: parseFloat(depositForm.amount) });
      toast.success('Deposit request submitted! Pending admin approval.');
      setDepositForm({ amount: '', currency: 'USDT', payment_method: 'crypto_usdt_bep20', transaction_hash: '', upi_ref: '', bank_ref: '', notes: '' });
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const handleWithdraw = async () => {
    if (!withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      await api.post('/funds/client-withdraw', { ...withdrawForm, amount: parseFloat(withdrawForm.amount) });
      toast.success('Withdrawal request submitted! Pending admin approval.');
      setWithdrawForm({ amount: '', currency: 'USDT', payment_method: 'crypto_usdt_bep20', wallet_address: '', upi_id: '', bank_account_name: '', bank_account_no: '', bank_ifsc: '', notes: '' });
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const depConvert = depositForm.amount ? (depositForm.currency === 'INR' ? `~ ${(parseFloat(depositForm.amount) / exchangeRate).toFixed(2)} USDT` : `~ ₹${(parseFloat(depositForm.amount) * exchangeRate).toFixed(0)} INR`) : '';
  const wdConvert = withdrawForm.amount ? (withdrawForm.currency === 'INR' ? `~ ${(parseFloat(withdrawForm.amount) / exchangeRate).toFixed(2)} USDT` : `~ ₹${(parseFloat(withdrawForm.amount) * exchangeRate).toFixed(0)} INR`) : '';

  const PAYMENT_METHODS = paymentConfig?.methods || [
    { value: 'crypto_usdt_bep20', label: 'Crypto — USDT BEP20' },
    { value: 'crypto_usdt_trc20', label: 'Crypto — USDT TRC20' },
    { value: 'upi', label: 'UPI Transfer' },
    { value: 'bank_transfer', label: 'Bank Transfer (NEFT/IMPS)' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'cash', label: 'Cash (Branch Deposit)' },
  ];
  const activeMethodDetails = PAYMENT_METHODS.find(m => m.value === depositForm.payment_method)?.details;

  const bal = data?.balance || {};
  const calendarDates = calendarData.reduce((acc, d) => { acc[d.date] = d; return acc; }, {});
  const selectedDayData = selectedDate ? calendarDates[selectedDate.toISOString().split('T')[0]] : null;

  if (loading) return <div className="flex items-center justify-center h-screen bg-brand-bg"><div className="animate-spin w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="investor-portal">
      {/* Mobile sticky header — Groww-style */}
      <MobileHeader
        userName={user?.name || data?.client?.name || ''}
        onBellClick={async () => {
          try {
            await api.put('/notifications/in-app/read-all');
            toast.success('Notifications marked as read');
          } catch (_) {}
        }}
        onProfileClick={() => setActiveTab('profile')}
        onLogout={handleLogout}
        onSpotClick={() => navigate('/investor/trading')}
        onFuturesClick={() => navigate('/investor/futures')}
      />
      <header className="hidden md:flex bg-white border-b border-brand-border px-4 sm:px-6 py-3 items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img src="/quantora-logo.png" alt="Quantora" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="font-heading text-lg font-semibold text-brand-primary">Quantora Analytics</h1>
            <p className="text-xs text-brand-text-secondary">Investor Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button size="sm" onClick={() => navigate('/investor/trading')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm h-8 px-3 text-xs font-semibold" data-testid="investor-trading-btn">
            <Activity size={13} className="mr-1.5" /> Spot
          </Button>
          <Button size="sm" onClick={() => navigate('/investor/futures')} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm h-8 px-3 text-xs font-semibold" data-testid="investor-futures-btn">
            <Activity size={13} className="mr-1.5" /> Futures
          </Button>
          <span className="text-sm text-brand-text-secondary hidden sm:block">{user?.name}</span>
          <Button variant="outline" size="sm" onClick={handleLogout} className="border-brand-border rounded-sm" data-testid="investor-logout-btn">
            <LogOut size={14} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {!data?.client ? (
          <Card className="border border-brand-border bg-white mt-8">
            <CardContent className="p-12 text-center">
              <p className="text-brand-text-secondary text-lg">No client account linked. Please contact admin.</p>
            </CardContent>
          </Card>
        ) : (!data.client?.kyc_status || data.client.kyc_status === 'not_submitted') && activeTab !== 'kyc' ? (
          /* ─── FIRST-TIME KYC GATE — no submission yet ─── */
          <div className="min-h-[60vh] flex items-center justify-center py-12" data-testid="kyc-first-time-gate">
            <Card className="border border-brand-primary/20 bg-gradient-to-br from-brand-primary/5 to-white max-w-xl w-full shadow-sm">
              <CardContent className="p-8 sm:p-10 text-center space-y-5">
                <div className="mx-auto w-16 h-16 rounded-full bg-brand-primary/10 flex items-center justify-center">
                  <ShieldCheck size={30} className="text-brand-primary" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-heading text-2xl font-bold text-brand-text-primary">Welcome to Quantora, {data.client?.name?.split(' ')[0] || 'Investor'} 👋</h2>
                  <p className="text-sm text-brand-text-secondary max-w-md mx-auto leading-relaxed">
                    Please complete your KYC to unlock your investor dashboard, funding and trades.
                  </p>
                </div>
                <div className="bg-white border border-brand-border rounded-lg p-4 text-left space-y-2 max-w-sm mx-auto">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">Your Account No.</span>
                    <span className="font-mono font-semibold text-brand-primary">{formatClientCode(data.client)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">Time to complete</span>
                    <span className="font-semibold text-brand-text-primary">~ 3 minutes</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">You'll need</span>
                    <span className="text-brand-text-primary">PAN + Aadhaar + Selfie</span>
                  </div>
                </div>
                <Button onClick={() => setActiveTab('kyc')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg h-11 px-8" data-testid="kyc-start-btn">
                  Start KYC now →
                </Button>
                <p className="text-[10px] text-brand-text-secondary">
                  Verification typically takes 1–24 hours after submission.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (activeTab !== 'kyc' && data.client?.kyc_status === 'pending') ? (
          /* ─── KYC PENDING GATE — block dashboard until verified ─── */
          <div className="min-h-[60vh] flex items-center justify-center py-12" data-testid="kyc-pending-gate">
            <Card className="border border-amber-200 bg-gradient-to-br from-amber-50 to-white max-w-xl w-full shadow-sm">
              <CardContent className="p-8 sm:p-10 text-center space-y-5">
                <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center relative">
                  <ShieldCheck size={30} className="text-amber-600" />
                  <span className="absolute inset-0 rounded-full border-2 border-amber-400 animate-ping opacity-30" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-heading text-2xl font-bold text-brand-text-primary">KYC Verification In Progress</h2>
                  <p className="text-sm text-brand-text-secondary max-w-md mx-auto leading-relaxed">
                    Your KYC has been submitted successfully. Our compliance team is reviewing your documents.
                  </p>
                </div>
                <div className="bg-white border border-amber-200 rounded-lg p-4 text-left space-y-2 max-w-sm mx-auto">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">Estimated verification time</span>
                    <span className="font-semibold text-amber-700">1 – 24 hours</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">Client ID</span>
                    <span className="font-mono font-semibold text-brand-primary">{formatClientCode(data.client)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-brand-text-secondary">Status</span>
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">Pending Review</Badge>
                  </div>
                </div>
                <p className="text-xs text-brand-text-secondary italic">
                  You will receive an email + in-app notification once your KYC is approved. Dashboard access will unlock automatically. Please wait.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('kyc')} data-testid="kyc-review-btn" className="rounded-lg">Review My KYC</Button>
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('support')} data-testid="kyc-contact-support-btn" className="rounded-lg">Contact Support</Button>
                  <Button variant="ghost" size="sm" onClick={() => window.location.reload()} data-testid="kyc-refresh-btn" className="text-brand-primary">Refresh Status</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
          {/* KYC Pending Banner */}
          {data.client?.kyc_status !== 'verified' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4 mb-2 flex items-center gap-3" data-testid="kyc-pending-banner">
              <ShieldCheck size={20} className="text-amber-600 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">
                  {data.client?.kyc_status === 'pending' ? 'KYC Verification Pending' : data.client?.kyc_status === 'rejected' ? 'KYC Rejected — Please Resubmit' : 'Complete Your KYC'}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">Please complete your KYC verification to access all features. Go to the KYC tab to submit your documents.</p>
              </div>
            </div>
          )}
          <Tabs value={activeTab ?? (data.client?.kyc_status === 'verified' ? 'dashboard' : 'kyc')} onValueChange={setActiveTab} className="mt-4 pb-24 md:pb-4">
            <TabsList className="hidden md:flex bg-white border border-brand-border rounded-sm p-1 h-auto flex-wrap gap-1 mb-6">
              {[
                { v: 'dashboard', l: 'Dashboard' }, { v: 'plans', l: 'ETF Plans' }, { v: 'trades', l: 'Trade History' }, { v: 'transactions', l: 'Transactions' },
                { v: 'deposit', l: 'Deposit' }, { v: 'withdraw', l: 'Withdraw' },
                { v: 'calendar', l: 'Calendar' }, { v: 'kyc', l: 'KYC' }, { v: 'profile', l: 'Profile' }, { v: 'support', l: 'Support' }
              ].map(t => (
                <TabsTrigger key={t.v} value={t.v} className="rounded-sm text-xs sm:text-sm data-[state=active]:bg-brand-primary data-[state=active]:text-white px-3 py-1.5" data-testid={`tab-${t.v}`}>
                  {t.l}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* Mobile active-tab label */}
            <div className="md:hidden mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-brand-text-primary capitalize">
                {(activeTab ?? (data.client?.kyc_status === 'verified' ? 'dashboard' : 'kyc')).replace(/-/g, ' ')}
              </h2>
            </div>

            {/* DASHBOARD TAB */}
            <TabsContent value="dashboard" className="space-y-6">
              <h2 className="font-heading text-xl font-bold text-brand-text-primary">Welcome, {data.client.name} <span className="text-brand-primary font-mono text-sm ml-2" title={data.client.client_id}>({formatClientCode(data.client)})</span></h2>

              {/* Period toggle */}
              {(() => {
                const now = new Date();
                // Resolve the selected period into a filter + label
                let selectedYear = now.getFullYear();
                let selectedMonth = now.getMonth();
                let showAllTime = false;
                let periodLabel;
                if (period === 'current_month') {
                  periodLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
                } else if (period === 'all') {
                  showAllTime = true;
                  periodLabel = 'All Time';
                } else if (/^\d{4}-\d{2}$/.test(period)) {
                  const [yr, mo] = period.split('-');
                  selectedYear = parseInt(yr);
                  selectedMonth = parseInt(mo) - 1;
                  periodLabel = new Date(selectedYear, selectedMonth, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                } else {
                  periodLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
                }
                const isCurrentPeriod = period === 'current_month';
                const entries = data.pnl_entries || [];
                const monthEntries = showAllTime ? entries : entries.filter(e => {
                  if (!e.date) return false;
                  const d = new Date(e.date);
                  return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth;
                });
                const monthProfit = monthEntries.reduce((s, e) => s + (e.gross_profit || 0), 0);
                const monthLoss = monthEntries.reduce((s, e) => s + (e.loss || 0), 0);
                const monthNetPnl = monthProfit - monthLoss;
                const monthWinTrades = monthEntries.filter(e => (e.net_pnl || 0) > 0).length;
                const monthWinRate = monthEntries.length > 0 ? Math.round(monthWinTrades / monthEntries.length * 100) : 0;
                const netLabel = showAllTime ? 'Net P&L · All Time' : isCurrentPeriod ? 'Net P&L · This Month' : `Net P&L · ${periodLabel}`;
                const tradesLabel = showAllTime ? 'All-Time Trades' : isCurrentPeriod ? 'Month Trades' : `${periodLabel.split(' ')[0]} Trades`;
                const winLabel = showAllTime ? 'All-Time Win Rate' : isCurrentPeriod ? 'Month Win Rate' : `${periodLabel.split(' ')[0]} Win Rate`;
                const badgeText = showAllTime
                  ? `All Time`
                  : isCurrentPeriod
                  ? `Current Month · ${periodLabel}`
                  : `Historical · ${periodLabel}`;
                return (
                  <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Badge className="bg-brand-primary/10 text-brand-primary border-brand-primary/20 px-3 py-1.5 text-xs font-semibold" data-testid="current-period-badge">
                        {badgeText}
                      </Badge>
                      <div className="flex gap-2">
                        <Select value={period} onValueChange={setPeriod}>
                          <SelectTrigger className="w-44 bg-white border-brand-border h-8 text-xs rounded-sm" data-testid="statement-period-select">
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="current_month">Current Month</SelectItem>
                            {availableMonths.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                            <SelectItem value="all">All Time</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={handleDownloadStatement} disabled={statementLoading} className="h-8 text-xs rounded-sm border-brand-border" data-testid="download-statement-btn">
                          {statementLoading ? <Loader2 size={12} className="animate-spin mr-1" /> : <Download size={12} className="mr-1" />}
                          Download Statement
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                      <BalanceHero
                        mainBalance={bal.main_balance ?? bal.balance ?? 0}
                        netPnl={monthNetPnl}
                        pnlLabel={showAllTime ? 'All-time' : (isCurrentPeriod ? 'This Month' : periodLabel)}
                        activeInvestment={bal.active_investment ?? bal.total_investment ?? 0}
                        onDeposit={() => setActiveTab('deposit')}
                        onWithdraw={() => setActiveTab('withdraw')}
                      />
                    </div>
                    <QuickActionGrid onAction={(id) => {
                      if (id === 'statement') { handleDownloadStatement(); return; }
                      setActiveTab(id);
                    }} />
                    <EnablePushButton variant="card" />
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <Card className="border border-brand-border bg-white" data-testid="month-net-pnl-card"><CardContent className="p-4 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{netLabel}</p>
                        <p className={`font-heading text-2xl font-bold ${monthNetPnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${monthNetPnl.toLocaleString()}</p>
                        <p className="text-[10px] text-brand-text-secondary mt-1">{monthEntries.length} trades</p>
                      </CardContent></Card>
                      <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">Total Withdrawn</p>
                        <p className="font-heading text-2xl font-bold text-brand-secondary">${(bal.total_withdrawal || 0).toLocaleString()}</p>
                      </CardContent></Card>
                      <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{tradesLabel}</p>
                        <p className="font-heading text-2xl font-bold text-brand-text-primary">{monthEntries.length}</p>
                      </CardContent></Card>
                      <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                        <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{winLabel}</p>
                        <p className="font-heading text-2xl font-bold text-brand-primary">{monthWinRate}%</p>
                      </CardContent></Card>
                    </div>
                  </>
                );
              })()}
              {/* Portfolio Growth Chart */}
              <Card className="border border-brand-border bg-white">
                <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-brand-text-primary">Portfolio Performance</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  {data.monthly_pnl?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.monthly_pnl}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="month" tick={AXIS_TICK} />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Bar dataKey="profit" name="Profit" fill="#059669" radius={BAR_RADIUS} />
                        <Bar dataKey="loss" name="Loss" fill="#DC2626" radius={BAR_RADIUS} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-brand-text-secondary text-sm py-8 text-center">No performance data yet</p>}
                </CardContent>
              </Card>
              {/* Equity Curve */}
              {data.daily_pnl?.length > 0 && (
                <Card className="border border-brand-border bg-white">
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-brand-text-primary">Equity Curve</CardTitle></CardHeader>
                  <CardContent className="p-4 pt-0">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={data.daily_pnl}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="date" tick={AXIS_TICK_SM} />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                        <Area type="monotone" dataKey="net" name="Net P&L" stroke="#2563EB" fill="#2563EB" fillOpacity={0.08} strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
              {/* Recent Trades Preview */}
              {data.pnl_entries?.length > 0 && (
                <Card className="border border-brand-border bg-white">
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-base text-brand-text-primary">Recent Trades</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                          {['Date', 'Gross Profit', 'Loss', 'Net P&L'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>)}
                        </tr></thead>
                        <tbody>
                          {data.pnl_entries.slice(0, 5).map((e, i) => {
                            const net = e.net_pnl || 0;
                            return (
                              <tr key={e.id || i} className="border-b border-brand-border/50 hover:bg-brand-surface-hover">
                                <td className="py-2.5 px-3 text-xs text-brand-text-secondary">{e.date}</td>
                                <td className="py-2.5 px-3 text-xs font-medium text-brand-success">+${(e.gross_profit || 0).toLocaleString()}</td>
                                <td className="py-2.5 px-3 text-xs font-medium text-brand-error">${(e.loss || 0).toLocaleString()}</td>
                                <td className={`py-2.5 px-3 text-xs font-bold ${net >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{net >= 0 ? '+' : ''}${net.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* TRADE HISTORY TAB */}
            <TabsContent value="plans" className="space-y-4" data-testid="plans-tab">
              <InvestmentPlansPanel
                walletBalance={data.client?.current_balance || 0}
                onWalletChanged={fetchData}
              />
            </TabsContent>

            <TabsContent value="trades" className="space-y-4" data-testid="trade-history-tab">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-brand-text-primary">Trade History & P&L</h2>
                <Badge className="bg-brand-primary/10 text-brand-primary border-0 text-xs px-3">{data.pnl_entries?.length || 0} trades</Badge>
              </div>
              {/* Trade Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {(() => {
                  const entries = data.pnl_entries || [];
                  const totalProfit = entries.reduce((s, e) => s + (e.gross_profit || 0), 0);
                  const totalLoss = entries.reduce((s, e) => s + (e.loss || 0), 0);
                  const netPnl = entries.reduce((s, e) => s + (e.net_pnl || 0), 0);
                  const wins = entries.filter(e => (e.net_pnl || 0) > 0).length;
                  const losses = entries.filter(e => (e.net_pnl || 0) < 0).length;
                  return [
                    { l: 'Total Profit', v: `$${totalProfit.toLocaleString()}`, c: 'text-brand-success' },
                    { l: 'Total Loss', v: `$${totalLoss.toLocaleString()}`, c: 'text-brand-error' },
                    { l: 'Net P&L', v: `$${netPnl.toLocaleString()}`, c: netPnl >= 0 ? 'text-brand-success' : 'text-brand-error' },
                    { l: 'Winning Trades', v: wins, c: 'text-brand-success' },
                    { l: 'Losing Trades', v: losses, c: 'text-brand-error' },
                  ].map(s => (
                    <Card key={s.l} className="border border-brand-border bg-white"><CardContent className="p-3 text-center">
                      <p className="text-[10px] uppercase tracking-widest text-brand-text-secondary font-semibold">{s.l}</p>
                      <p className={`font-heading text-lg font-bold ${s.c}`}>{s.v}</p>
                    </CardContent></Card>
                  ));
                })()}
              </div>
              {/* Full Trade List — mobile-first rich cards + segmented filter */}
              {(() => {
                const entries = data.pnl_entries || [];
                const counts = {
                  all: entries.length,
                  wins: entries.filter((e) => (e.net_pnl || 0) > 0).length,
                  losses: entries.filter((e) => (e.net_pnl || 0) < 0).length,
                  spot: entries.filter((e) => e.source === 'trade').length,
                  futures: entries.filter((e) => e.source === 'futures_trade' || e.source === 'futures_manual').length,
                };
                const filtered = entries.filter((e) => {
                  const net = e.net_pnl || 0;
                  if (tradeFilter === 'wins') return net > 0;
                  if (tradeFilter === 'losses') return net < 0;
                  if (tradeFilter === 'spot') return e.source === 'trade';
                  if (tradeFilter === 'futures') return e.source === 'futures_trade' || e.source === 'futures_manual';
                  return true;
                });
                return (
                  <>
                    <SegmentedTradeFilter value={tradeFilter} onChange={setTradeFilter} counts={counts} />
                    <div className="space-y-2.5" data-testid="trade-card-list">
                      {filtered.length === 0 ? (
                        <div className="py-12 text-center bg-white border border-brand-border rounded-xl">
                          <p className="text-sm text-brand-text-secondary">No trades matching this filter.</p>
                        </div>
                      ) : (
                        filtered.map((e, i) => <TradeRowCard key={e.id || i} trade={e} index={i} />)
                      )}
                    </div>
                  </>
                );
              })()}
            </TabsContent>

            {/* TRANSACTIONS TAB */}
            <TabsContent value="transactions" className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Deposits</p>
                  <p className="font-heading text-xl text-brand-success">${(bal.deposits || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Withdrawals</p>
                  <p className="font-heading text-xl text-brand-error">${(bal.withdrawals || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Net P&L</p>
                  <p className={`font-heading text-xl ${(bal.net_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${(bal.net_pnl || 0).toLocaleString()}</p>
                </CardContent></Card>
                <Card className="border border-brand-border bg-white"><CardContent className="p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Balance</p>
                  <p className="font-heading text-xl text-brand-primary">${(bal.balance || 0).toLocaleString()}</p>
                </CardContent></Card>
              </div>
              <Card className="border border-brand-border bg-white">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="investor-txn-table">
                      <thead>
                        <tr className="border-b border-brand-border bg-brand-bg/50">
                          {['Date', 'Type', 'Amount', 'Status', 'TXID', 'Notes'].map(h => (
                            <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.transactions?.length === 0 ? (
                          <tr><td colSpan={6} className="py-8 text-center text-brand-text-secondary">No transactions yet</td></tr>
                        ) : data.transactions?.map((t, i) => (
                          <tr key={t.id || i} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                            <td className="py-2.5 px-3 text-brand-text-secondary text-xs">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                            <td className="py-2.5 px-3">
                              <Badge className={`text-xs border-0 capitalize ${['deposit', 'profit_credit'].includes(t.type) ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>
                                {t.type?.replace('_', ' ')}
                              </Badge>
                            </td>
                            <td className={`py-2.5 px-3 font-medium ${['deposit', 'profit_credit'].includes(t.type) ? 'text-brand-success' : 'text-brand-error'}`}>
                              {['deposit', 'profit_credit'].includes(t.type) ? '+' : '-'}${Number(t.amount).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-3">
                              <Badge className={`text-xs border-0 ${t.status === 'approved' ? 'bg-brand-success/15 text-brand-success' : t.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-brand-error/15 text-brand-error'}`}>{t.status}</Badge>
                            </td>
                            <td className="py-2.5 px-3 text-brand-text-secondary text-xs font-mono max-w-[120px] truncate">{t.transaction_hash || '-'}</td>
                            <td className="py-2.5 px-3 text-brand-text-secondary text-xs max-w-[150px] truncate">{t.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* DEPOSIT TAB */}
            <TabsContent value="deposit" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border border-brand-border bg-white">
                  <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary">
                    {depositForm.payment_method?.startsWith('crypto') ? 'Deposit Wallet (Crypto)' :
                     depositForm.payment_method === 'upi' ? 'Company UPI Details' :
                     depositForm.payment_method === 'bank_transfer' ? 'Company Bank Details' : 'Deposit Info'}
                  </CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {depositForm.payment_method?.startsWith('crypto') && (
                      <div className="bg-brand-bg border border-brand-border rounded-lg p-4 text-center">
                        <p className="text-xs uppercase tracking-wider text-brand-text-secondary mb-3">Send USDT to this address</p>
                        <div className="bg-white p-3 rounded-lg inline-block mb-3"><QRCode value={activeMethodDetails?.address || depositSettings.deposit_wallet_address || ''} size={140} /></div>
                        <div className="flex items-center gap-2 bg-white border border-brand-border rounded-lg p-2">
                          <code className="text-xs text-brand-text-primary flex-1 break-all" data-testid="wallet-address">{activeMethodDetails?.address || depositSettings.deposit_wallet_address || 'Not configured'}</code>
                          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(activeMethodDetails?.address || depositSettings.deposit_wallet_address); toast.success('Copied'); }}><Copy size={14} /></Button>
                        </div>
                        <Badge className="text-xs bg-brand-primary/10 text-brand-primary border-0 mt-2">Network: {activeMethodDetails?.network || depositSettings.deposit_network || 'BEP20'}</Badge>
                      </div>
                    )}
                    {depositForm.payment_method === 'upi' && activeMethodDetails && (
                      <div className="bg-brand-bg border border-brand-border rounded-lg p-4 space-y-2">
                        <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Pay to this UPI</p>
                        <div className="flex items-center gap-2 bg-white border border-brand-border rounded-lg p-3">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-brand-text-primary">{activeMethodDetails.upi_name || 'QUANTORA ANALYTICS'}</p>
                            <p className="text-sm font-mono text-brand-primary">{activeMethodDetails.upi_id || '-'}</p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(activeMethodDetails.upi_id); toast.success('UPI ID copied'); }}><Copy size={14} /></Button>
                        </div>
                      </div>
                    )}
                    {depositForm.payment_method === 'bank_transfer' && activeMethodDetails && (
                      <div className="bg-brand-bg border border-brand-border rounded-lg p-4 space-y-2">
                        <p className="text-xs uppercase tracking-wider text-brand-text-secondary">Transfer to this account</p>
                        <div className="bg-white border border-brand-border rounded-lg p-3 space-y-1.5 text-sm">
                          {[
                            ['Bank', activeMethodDetails.bank_name],
                            ['Account Name', activeMethodDetails.account_name],
                            ['Account No', activeMethodDetails.account_no],
                            ['IFSC Code', activeMethodDetails.ifsc],
                            ['Branch', activeMethodDetails.branch],
                            ['Type', activeMethodDetails.account_type],
                          ].filter(([,v]) => v).map(([l,v]) => (
                            <div key={l} className="flex justify-between"><span className="text-brand-text-secondary text-xs">{l}</span><span className="font-medium text-brand-text-primary text-xs font-mono">{v}</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(depositForm.payment_method === 'cheque' || depositForm.payment_method === 'cash') && (
                      <div className="bg-brand-bg border border-brand-border rounded-lg p-4 text-center">
                        <p className="text-sm text-brand-text-secondary">Please visit your nearest branch for {depositForm.payment_method === 'cheque' ? 'cheque' : 'cash'} deposit. Submit reference details below.</p>
                      </div>
                    )}
                    <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50 text-center">
                      <p className="text-xs text-brand-text-secondary">Live Rate: <span className="font-semibold text-brand-text-primary">1 USDT = ₹{exchangeRate}</span></p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-brand-border bg-white">
                  <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary">Submit Deposit</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label className="text-xs">Currency</Label>
                        <Select value={depositForm.currency} onValueChange={v => setDepositForm({...depositForm, currency: v})}>
                          <SelectTrigger className="bg-white border-brand-border rounded-lg h-9 text-sm" data-testid="deposit-currency"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="USDT">USDT</SelectItem><SelectItem value="INR">INR (₹)</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-xs">Amount *</Label>
                        <Input type="number" value={depositForm.amount} onChange={e => setDepositForm({...depositForm, amount: e.target.value})} placeholder="0.00" data-testid="deposit-amount" className="bg-white border-brand-border rounded-lg h-9 text-sm" />
                        {depConvert && <p className="text-[10px] text-brand-primary font-medium">{depConvert}</p>}
                      </div>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Payment Method *</Label>
                      <Select value={depositForm.payment_method} onValueChange={v => setDepositForm({...depositForm, payment_method: v})}>
                        <SelectTrigger className="bg-white border-brand-border rounded-lg h-9 text-sm" data-testid="deposit-method"><SelectValue /></SelectTrigger>
                        <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {depositForm.payment_method.startsWith('crypto') && (
                      <div className="space-y-1.5"><Label className="text-xs">Transaction Hash (TXID)</Label><Input value={depositForm.transaction_hash} onChange={e => setDepositForm({...depositForm, transaction_hash: e.target.value})} placeholder="0x..." data-testid="deposit-txid" className="bg-white border-brand-border rounded-lg h-9 text-sm font-mono" /></div>
                    )}
                    {depositForm.payment_method === 'upi' && (
                      <div className="space-y-1.5"><Label className="text-xs">UPI Reference / UTR Number</Label><Input value={depositForm.upi_ref} onChange={e => setDepositForm({...depositForm, upi_ref: e.target.value})} placeholder="UTR123456" className="bg-white border-brand-border rounded-lg h-9 text-sm" /></div>
                    )}
                    {depositForm.payment_method === 'bank_transfer' && (
                      <div className="space-y-1.5"><Label className="text-xs">Bank Reference / Transaction No</Label><Input value={depositForm.bank_ref} onChange={e => setDepositForm({...depositForm, bank_ref: e.target.value})} placeholder="NEFT/IMPS ref" className="bg-white border-brand-border rounded-lg h-9 text-sm" /></div>
                    )}
                    <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={depositForm.notes} onChange={e => setDepositForm({...depositForm, notes: e.target.value})} placeholder="Optional" rows={2} className="bg-white border-brand-border rounded-lg text-sm" /></div>
                    <Button onClick={handleDeposit} disabled={submitting} className="w-full bg-brand-success hover:bg-brand-success/90 text-white rounded-lg h-10" data-testid="submit-deposit-btn">
                      {submitting ? 'Submitting...' : 'Submit Deposit Request'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* WITHDRAW TAB */}
            <TabsContent value="withdraw" className="max-w-lg">
              <Card className="border border-brand-border bg-white">
                <CardHeader>
                  <CardTitle className="font-heading text-base text-brand-text-primary">Request Withdrawal</CardTitle>
                  <p className="text-xs text-brand-text-secondary">Withdrawable: <strong className="text-brand-primary">${(bal.main_balance ?? bal.withdrawable ?? 0).toLocaleString()}</strong> USDT
                    <span className="text-brand-text-secondary ml-1">(~ ₹{((bal.main_balance ?? bal.withdrawable ?? 0) * exchangeRate).toLocaleString()})</span>
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label className="text-xs">Currency</Label>
                      <Select value={withdrawForm.currency} onValueChange={v => setWithdrawForm({...withdrawForm, currency: v})}>
                        <SelectTrigger className="bg-white border-brand-border rounded-lg h-9 text-sm" data-testid="withdraw-currency"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="USDT">USDT</SelectItem><SelectItem value="INR">INR (₹)</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label className="text-xs">Amount *</Label>
                      <Input type="number" value={withdrawForm.amount} onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})} placeholder="0.00" data-testid="withdraw-amount" className="bg-white border-brand-border rounded-lg h-9 text-sm" />
                      {wdConvert && <p className="text-[10px] text-brand-primary font-medium">{wdConvert}</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Withdrawal Method *</Label>
                    <Select value={withdrawForm.payment_method} onValueChange={v => setWithdrawForm({...withdrawForm, payment_method: v})}>
                      <SelectTrigger className="bg-white border-brand-border rounded-lg h-9 text-sm" data-testid="withdraw-method"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {withdrawForm.payment_method.startsWith('crypto') && (
                    <div className="space-y-1.5"><Label className="text-xs">Wallet Address *</Label><Input value={withdrawForm.wallet_address} onChange={e => setWithdrawForm({...withdrawForm, wallet_address: e.target.value})} placeholder="0x..." data-testid="withdraw-address" className="bg-white border-brand-border rounded-lg h-9 text-sm font-mono" /></div>
                  )}
                  {withdrawForm.payment_method === 'upi' && (
                    <div className="space-y-1.5"><Label className="text-xs">UPI ID *</Label><Input value={withdrawForm.upi_id} onChange={e => setWithdrawForm({...withdrawForm, upi_id: e.target.value})} placeholder="name@upi" className="bg-white border-brand-border rounded-lg h-9 text-sm" /></div>
                  )}
                  {withdrawForm.payment_method === 'bank_transfer' && (
                    <div className="space-y-3">
                      <div className="space-y-1.5"><Label className="text-xs">Account Holder Name *</Label><Input value={withdrawForm.bank_account_name} onChange={e => setWithdrawForm({...withdrawForm, bank_account_name: e.target.value})} className="bg-white border-brand-border rounded-lg h-9 text-sm" /></div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label className="text-xs">Account No *</Label><Input value={withdrawForm.bank_account_no} onChange={e => setWithdrawForm({...withdrawForm, bank_account_no: e.target.value})} className="bg-white border-brand-border rounded-lg h-9 text-sm font-mono" /></div>
                        <div className="space-y-1.5"><Label className="text-xs">IFSC Code *</Label><Input value={withdrawForm.bank_ifsc} onChange={e => setWithdrawForm({...withdrawForm, bank_ifsc: e.target.value.toUpperCase()})} className="bg-white border-brand-border rounded-lg h-9 text-sm font-mono" /></div>
                      </div>
                    </div>
                  )}
                  {/* Auto-fill from saved details */}
                  {paymentDetails && (
                    <div className="bg-brand-bg/60 rounded-lg p-2.5 border border-brand-border/50">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Saved Details</p>
                      <div className="flex flex-wrap gap-1.5">
                        {withdrawForm.payment_method.startsWith('crypto') && paymentDetails.crypto_wallets?.map((w, i) => (
                          <Button key={i} variant="outline" size="sm" className="rounded-lg text-[10px] h-6" onClick={() => setWithdrawForm({...withdrawForm, wallet_address: w.address})}>{w.label || `Wallet ${i+1}`}</Button>
                        ))}
                        {withdrawForm.payment_method === 'upi' && paymentDetails.upi_ids?.map((u, i) => (
                          <Button key={i} variant="outline" size="sm" className="rounded-lg text-[10px] h-6" onClick={() => setWithdrawForm({...withdrawForm, upi_id: u.upi_id})}>{u.label || u.upi_id}</Button>
                        ))}
                        {withdrawForm.payment_method === 'bank_transfer' && paymentDetails.bank_accounts?.map((b, i) => (
                          <Button key={i} variant="outline" size="sm" className="rounded-lg text-[10px] h-6" onClick={() => setWithdrawForm({...withdrawForm, bank_account_name: b.name, bank_account_no: b.account_no, bank_ifsc: b.ifsc})}>{b.label || b.bank_name}</Button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={withdrawForm.notes} onChange={e => setWithdrawForm({...withdrawForm, notes: e.target.value})} rows={2} className="bg-white border-brand-border rounded-lg text-sm" /></div>
                  <Button onClick={handleWithdraw} disabled={submitting} className="w-full bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-lg h-10" data-testid="submit-withdraw-btn">
                    {submitting ? 'Submitting...' : 'Submit Withdrawal Request'}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* CALENDAR TAB — P&L Heatmap */}
            <TabsContent value="calendar" className="space-y-4" data-testid="calendar-tab">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
                <div className="lg:col-span-3">
                  <PnlHeatmapCalendar
                    data={calendarData}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    onDateSelect={setSelectedDate}
                    selectedDate={selectedDate}
                  />
                </div>
                <Card className="border border-brand-border bg-white lg:col-span-2 h-fit">
                  <CardHeader>
                    <CardTitle className="font-heading text-base text-brand-text-primary">
                      {selectedDate ? `${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}` : 'Day Details'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedDayData ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-2">
                          <div className="rounded-xl bg-emerald-50 p-3 text-center">
                            <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Profit</p>
                            <p className="font-bold text-emerald-700 text-base">${(selectedDayData.gross_profit || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                          </div>
                          <div className="rounded-xl bg-rose-50 p-3 text-center">
                            <p className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Loss</p>
                            <p className="font-bold text-rose-700 text-base">${(selectedDayData.loss || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                          </div>
                          <div className={`rounded-xl p-3 text-center ${selectedDayData.net_pnl >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">Net P&L</p>
                            <p className={`font-bold text-base ${selectedDayData.net_pnl >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {selectedDayData.net_pnl >= 0 ? '+' : ''}${(selectedDayData.net_pnl || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2 pt-1">
                          <p className="text-[11px] uppercase tracking-widest text-brand-text-secondary font-semibold">
                            Trades ({selectedDayData.entries?.length || 0})
                          </p>
                          {(selectedDayData.entries || []).map((e, i) => (
                            <div key={e.id || e._id || `${e.date}-${e.symbol || e.asset_name || 'row'}-${i}`} className="bg-brand-bg/60 rounded-xl p-3 text-xs space-y-1 border border-brand-border/60">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-brand-text-primary">{e.symbol || e.asset_name || '-'}</span>
                                  {e.direction && (
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${(e.direction || '').toLowerCase() === 'long' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                      {(e.direction || '').toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <span className={`font-semibold ${(e.net_pnl || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {(e.net_pnl || 0) >= 0 ? '+' : ''}${Number(e.net_pnl || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-brand-text-secondary">
                                <span>Profit ${Number(e.gross_profit || 0).toFixed(2)}</span>
                                <span>Loss ${Number(e.loss || 0).toFixed(2)}</span>
                              </div>
                              {e.notes && <p className="text-brand-text-secondary italic">"{e.notes}"</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="py-10 text-center">
                        <CalIcon size={32} className="mx-auto text-brand-text-secondary/40 mb-2" />
                        <p className="text-sm text-brand-text-secondary">Tap any day on the heatmap</p>
                        <p className="text-xs text-brand-text-secondary mt-1">Darker green = bigger profit · Darker red = bigger loss</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* KYC TAB */}
            <TabsContent value="kyc">
              <Card className="border border-brand-border bg-white max-w-2xl" data-testid="investor-kyc-section">
                <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary flex items-center gap-2"><ShieldCheck size={18} /> KYC Verification</CardTitle></CardHeader>
                <CardContent>
                  {data?.client?.id ? (
                    <KycFormSection clientId={data.client.id} />
                  ) : (
                    <p className="text-sm text-brand-text-secondary">No client account linked.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* PROFILE TAB */}
            <TabsContent value="profile">
              <Card className="border border-brand-border bg-white max-w-lg">
                <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary flex items-center gap-2"><User size={18} /> My Profile</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { l: 'Client ID', v: formatClientCode(data.client) },
                    { l: 'Name', v: data.client.name },
                    { l: 'Email', v: data.client.email },
                    { l: 'Phone', v: data.client.phone || '-' },
                    { l: 'Gross Investment (all-time)', v: `$${(bal.total_investment || 0).toLocaleString()}` },
                    { l: 'Current Investment', v: `$${(bal.active_investment ?? bal.total_investment ?? 0).toLocaleString()}` },
                    { l: 'Total Withdrawal', v: `$${(bal.total_withdrawal || 0).toLocaleString()}` },
                    { l: 'Net Profit', v: `$${(bal.net_pnl || 0).toLocaleString()}` },
                    { l: 'Current Balance', v: `$${(bal.balance || 0).toLocaleString()}` },
                    { l: 'KYC Status', v: data.client.kyc_status || 'pending' },
                    { l: 'Account Status', v: data.client.status || 'active' },
                  ].map(row => (
                    <div key={row.l} className="flex justify-between py-2 border-b border-brand-border/50">
                      <span className="text-sm text-brand-text-secondary">{row.l}</span>
                      <span className="text-sm font-medium text-brand-text-primary">{row.v}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUPPORT TAB */}
            <TabsContent value="support">
              <Card className="border border-brand-border bg-white max-w-lg" data-testid="investor-support-section">
                <CardHeader><CardTitle className="font-heading text-base text-brand-text-primary flex items-center gap-2"><Phone size={18} /> Support & Contact</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {supportInfo?.branch && (
                    <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-2">
                      <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><Building2 size={13} /> Your Branch Office</p>
                      <p className="text-sm font-medium text-brand-text-primary">{supportInfo.branch.name}{supportInfo.branch.city ? `, ${supportInfo.branch.city}` : ''}</p>
                      {supportInfo.branch.address && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><MapPin size={12} /> {supportInfo.branch.address}</p>}
                      {supportInfo.branch.phone && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Phone size={12} /> <a href={`tel:${supportInfo.branch.phone}`} className="underline">{supportInfo.branch.phone}</a></p>}
                      {supportInfo.branch.email && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Mail size={12} /> <a href={`mailto:${supportInfo.branch.email}`} className="underline">{supportInfo.branch.email}</a></p>}
                    </div>
                  )}
                  {supportInfo?.manager && (
                    <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-2">
                      <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">Your Manager</p>
                      <p className="text-sm font-medium text-brand-text-primary">{supportInfo.manager.name}</p>
                      {supportInfo.manager.phone && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Phone size={12} /> <a href={`tel:${supportInfo.manager.phone}`} className="underline">{supportInfo.manager.phone}</a></p>}
                      {supportInfo.manager.email && <p className="text-xs text-brand-text-secondary flex items-center gap-1.5"><Mail size={12} /> <a href={`mailto:${supportInfo.manager.email}`} className="underline">{supportInfo.manager.email}</a></p>}
                    </div>
                  )}
                  <div className="bg-brand-bg/60 rounded-lg p-4 border border-brand-border/50 space-y-1">
                    <p className="text-xs uppercase tracking-wider font-semibold text-brand-text-secondary flex items-center gap-1.5"><Globe size={13} /> Company</p>
                    <p className="text-sm font-medium text-brand-text-primary">QUANTORA ANALYTICS PRIVATE LIMITED</p>
                    <p className="text-xs text-brand-text-secondary">quantora.co.in</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          </>
        )}
      </div>
      <div className="hidden md:flex items-center justify-center gap-2 py-3 px-4 border-t border-brand-border bg-white">
        <p className="text-[11px] text-brand-text-secondary font-semibold tracking-[0.2em]">QUANTORA · MARKET ADVISORY</p>
      </div>
      <div className="md:hidden flex items-center justify-center gap-1.5 py-2 pb-20 bg-white border-t border-brand-border">
        <p className="text-[10px] font-semibold text-brand-text-secondary tracking-[0.2em]">QUANTORA · MARKET ADVISORY</p>
      </div>
      {/* Mobile bottom navigation bar — appears only on mobile (<md) */}
      <GrowwBottomNav
        activeTab={activeTab ?? (data?.client?.kyc_status === 'verified' ? 'dashboard' : 'kyc')}
        onTabChange={setActiveTab}
        moreOpen={moreOpen}
        setMoreOpen={setMoreOpen}
      />
    </div>
  );
}
